import { updateGroupSize } from '../action/group';
import { EventTypes, TYPES } from '../render/constants';
import { Rect } from './region';

// Helper functions for advanced alignment features
export function calculateBounds(rects) {
  const minX = Math.min(...rects.map((r) => r.x));
  const minY = Math.min(...rects.map((r) => r.y));
  const maxX = Math.max(...rects.map((r) => r.x + r.w));
  const maxY = Math.max(...rects.map((r) => r.y + r.h));

  return { x: minX, y: minY, w: maxX - minX, h: maxY - minY };
}

export function calculateGaps(rects, direction) {
  const sorted = [...rects].sort((a, b) => (direction === 'horizontal' ? a.x - b.x : a.y - b.y));

  const gaps = [];
  for (let i = 0; i < sorted.length - 1; i++) {
    const current = sorted[i];
    const next = sorted[i + 1];

    if (direction === 'horizontal') {
      gaps.push(next.x - (current.x + current.w));
    } else {
      gaps.push(next.y - (current.y + current.h));
    }
  }

  return gaps;
}

function detectHorizontalRow(rects, bounds) {
  const tolerance = bounds.h * 0.1;
  const centerY = bounds.y + bounds.h / 2;

  return rects.every((rect) => Math.abs(rect.y + rect.h / 2 - centerY) < tolerance);
}

function detectVerticalColumn(rects, bounds) {
  const tolerance = bounds.w * 0.1;
  const centerX = bounds.x + bounds.w / 2;

  return rects.every((rect) => Math.abs(rect.x + rect.w / 2 - centerX) < tolerance);
}

function detectGrid(rects, _bounds) {
  if (rects.length < 4) return false;

  // Simple grid detection based on regular spacing
  const rows = new Set();
  const cols = new Set();

  rects.forEach((rect) => {
    rows.add(Math.round(rect.y));
    cols.add(Math.round(rect.x));
  });

  return rows.size > 1 && cols.size > 1 && rows.size * cols.size === rects.length;
}

function detectConsistentSpacing(horizontalGaps, verticalGaps) {
  const checkConsistency = (gaps, tolerance = 5) => {
    if (gaps.length < 2) return false;
    const avgGap = gaps.reduce((sum, gap) => sum + gap, 0) / gaps.length;
    return gaps.every((gap) => Math.abs(gap - avgGap) < tolerance);
  };

  return {
    horizontal: checkConsistency(horizontalGaps),
    vertical: checkConsistency(verticalGaps),
  };
}

function detectExistingAlignment(rects) {
  const tolerance = 5;

  // Check for common alignment patterns
  const leftEdges = rects.map((r) => r.x);
  const rightEdges = rects.map((r) => r.x + r.w);
  const centerXs = rects.map((r) => r.x + r.w / 2);
  const topEdges = rects.map((r) => r.y);
  const bottomEdges = rects.map((r) => r.y + r.h);
  const centerYs = rects.map((r) => r.y + r.h / 2);

  return {
    horizontal: detectCommonValue(centerXs, tolerance)
      ? 'center'
      : detectCommonValue(leftEdges, tolerance)
        ? 'left'
        : detectCommonValue(rightEdges, tolerance)
          ? 'right'
          : null,
    vertical: detectCommonValue(centerYs, tolerance)
      ? 'center'
      : detectCommonValue(topEdges, tolerance)
        ? 'top'
        : detectCommonValue(bottomEdges, tolerance)
          ? 'bottom'
          : null,
  };
}

function detectCommonValue(values, tolerance) {
  if (values.length < 2) return false;
  const first = values[0];
  return values.every((value) => Math.abs(value - first) < tolerance);
}

function detectDistributionType(rects, bounds) {
  // Analyze how nodes are distributed in space
  const horizontalSpread = bounds.w / rects.length;
  const verticalSpread = bounds.h / rects.length;

  return {
    type: horizontalSpread > verticalSpread ? 'horizontal' : 'vertical',
    evenness: calculateDistributionEvenness(rects, bounds),
  };
}

function calculateDistributionEvenness(rects, _bounds) {
  // Calculate how evenly distributed the nodes are
  const sortedByX = [...rects].sort((a, b) => a.x - b.x);
  const sortedByY = [...rects].sort((a, b) => a.y - b.y);

  const xGaps = [];
  const yGaps = [];

  for (let i = 0; i < sortedByX.length - 1; i++) {
    xGaps.push(sortedByX[i + 1].x - (sortedByX[i].x + sortedByX[i].w));
  }

  for (let i = 0; i < sortedByY.length - 1; i++) {
    yGaps.push(sortedByY[i + 1].y - (sortedByY[i].y + sortedByY[i].h));
  }

  const xVariance = calculateVariance(xGaps);
  const yVariance = calculateVariance(yGaps);

  return { horizontal: 1 / (1 + xVariance), vertical: 1 / (1 + yVariance) };
}

// Analyze layout patterns for smart alignment decisions
export function analyzeLayoutPattern(stageContext, nodes) {
  const rects = nodes.map((node) => stageContext.api.getNodeRegion(node).rect);

  // Calculate layout metrics
  const bounds = calculateBounds(rects);
  const horizontalGaps = calculateGaps(rects, 'horizontal');
  const verticalGaps = calculateGaps(rects, 'vertical');

  // Detect patterns
  const patterns = {
    isHorizontalRow: detectHorizontalRow(rects, bounds),
    isVerticalColumn: detectVerticalColumn(rects, bounds),
    isGrid: detectGrid(rects, bounds),
    hasConsistentSpacing: detectConsistentSpacing(horizontalGaps, verticalGaps),
    alignment: detectExistingAlignment(rects),
    distribution: detectDistributionType(rects, bounds),
  };

  return {
    bounds,
    horizontalGaps,
    verticalGaps,
    patterns,
    metrics: {
      averageWidth: rects.reduce((sum, r) => sum + r.w, 0) / rects.length,
      averageHeight: rects.reduce((sum, r) => sum + r.h, 0) / rects.length,
      aspectRatioVariance: calculateAspectRatioVariance(rects),
    },
  };
}

function calculateVariance(values) {
  if (values.length === 0) return 0;
  const mean = values.reduce((sum, val) => sum + val, 0) / values.length;
  const variance = values.reduce((sum, val) => sum + Math.pow(val - mean, 2), 0) / values.length;
  return variance;
}

function calculateAspectRatioVariance(rects) {
  const ratios = rects.map((r) => r.w / r.h);
  return calculateVariance(ratios);
}

// Click-to-equalize action: when the equalize button is clicked, compute per-row and per-column uniform gaps
export function spacingEqualizeAction(hitTestContext) {
  const { type, targetHandler, stageContext, provider } = hitTestContext;
  if (type !== EventTypes.down) return false;
  if (targetHandler?.type !== 'spacing-equalize') return false;
  // Compute per-row uniform gaps (average of existing) and apply
  const rowGroups = computeRowGroups(stageContext);
  const colGroups = computeColGroups(stageContext);
  const avgGap = (gaps) => (gaps.length ? gaps.reduce((s, g) => s + g, 0) / gaps.length : 0);
  let rowTargets = rowGroups.map((g) => {
    const sorted = [...g.items].sort((a, b) => a.rect.x - b.rect.x);
    const gaps = [];
    for (let i = 0; i < sorted.length - 1; i++) {
      gaps.push(Math.max(0, sorted[i + 1].rect.x - (sorted[i].rect.x + sorted[i].rect.w)));
    }
    return { group: g, gap: avgGap(gaps) };
  });
  let colTargets = colGroups.map((g) => {
    const sorted = [...g.items].sort((a, b) => a.rect.y - b.rect.y);
    const gaps = [];
    for (let i = 0; i < sorted.length - 1; i++) {
      gaps.push(Math.max(0, sorted[i + 1].rect.y - (sorted[i].rect.y + sorted[i].rect.h)));
    }
    return { group: g, gap: avgGap(gaps) };
  });

  // Matrix support: when multiple rows/columns exist, use a single uniform gap per direction
  // Compute global horizontal gap across all horizontal adjacent pairs
  if (rowGroups.length > 1) {
    const pairsH = computeHorizontalPairs(stageContext);
    const hGaps = pairsH.map((p) => Math.max(0, p.bRect.x - (p.aRect.x + p.aRect.w)));
    const globalH = avgGap(hGaps);
    rowTargets = rowTargets.map((t) => ({ ...t, gap: globalH }));
  }
  // Compute global vertical gap across all vertical adjacent pairs
  if (colGroups.length > 1) {
    const pairsV = computeVerticalPairs(stageContext);
    const vGaps = pairsV.map((p) => Math.max(0, p.bRect.y - (p.aRect.y + p.aRect.h)));
    const globalV = avgGap(vGaps);
    colTargets = colTargets.map((t) => ({ ...t, gap: globalV }));
  }

  // Prepare caches for all affected nodes to ensure local-delta updates
  const interactive = stageContext.providers?.interactive || stageContext.interactive;
  const affectedIds = new Set([
    ...rowTargets.flatMap((t) => t.group.items.map((it) => it.node.id)),
    ...colTargets.flatMap((t) => t.group.items.map((it) => it.node.id)),
  ]);
  affectedIds.forEach((id) => interactive?.getMovingNodeInitialBox?.(id));

  // Apply using local deltas to avoid group-drift
  applyUniformGapUsingInitialRows(
    stageContext,
    rowTargets.map((t) => ({
      items: t.group.items.map((it) => ({ id: it.node.id, rect: it.rect })),
    })),
    null,
    rowTargets,
  );
  applyUniformGapUsingInitialCols(
    stageContext,
    colTargets.map((t) => ({
      items: t.group.items.map((it) => ({ id: it.node.id, rect: it.rect })),
    })),
    null,
    colTargets,
  );

  // Update group sizes once after all spacing changes
  const affectedNodesForGroup = Array.from(affectedIds)
    .map((id) => ({ node: stageContext.api.getNode(id) }))
    .filter((it) => it.node);
  updateGroupSize(stageContext, affectedNodesForGroup);
  stageContext.hooks.onNodeMoveCallback();
  stageContext.api.markNeedRender();
  provider.skipSelectFromUp = true;
  return true;
}

// Spacing handles: hit-test and drag to adjust equal spacing for multi-select
export function spacingAdjustAction(hitTestContext) {
  const { type, point, targetHandler, stageContext, provider } = hitTestContext;
  const interactive = provider;
  const selectedNodes = stageContext.api.getSelectedNodes().filter((n) => n.type !== TYPES.frame);
  if (!selectedNodes || selectedNodes.length < 2) return false;

  // Down on spacing handle
  if (type === EventTypes.down && targetHandler?.type === 'spacing') {
    interactive.movingHandler = targetHandler;
    interactive.pointStart = point;
    interactive.cursor.set(targetHandler.direction === 'horizontal' ? 'ew-resize' : 'ns-resize');
    // build spacing state for the specific pair
    const info = computeMultiSelectSpacingInfo(stageContext, targetHandler.direction);
    const pairKey = targetHandler.pairKey;
    const pairs =
      targetHandler.direction === 'horizontal'
        ? computeHorizontalPairs(stageContext)
        : computeVerticalPairs(stageContext);
    const pair = pairs.find((p) => p.key === pairKey);
    const { nodesSorted } = info;
    const initialRectsSorted = nodesSorted.map((n) => stageContext.api.getNodeRegion(n).rect);
    const leftRect = pair.aRect;
    const rightRect = pair.bRect;
    const initialGap =
      targetHandler.direction === 'horizontal'
        ? Math.max(0, rightRect.x - (leftRect.x + leftRect.w))
        : Math.max(0, rightRect.y - (leftRect.y + leftRect.h));
    // capture initial row/col groups for linked layout during drag
    const initialRowGroups = computeRowGroups(stageContext).map((g) => ({
      items: g.items.map((it) => ({
        id: it.node.id,
        rect: { ...stageContext.api.getNodeRegion(it.node).rect },
      })),
    }));
    const initialColGroups = computeColGroups(stageContext).map((g) => ({
      items: g.items.map((it) => ({
        id: it.node.id,
        rect: { ...stageContext.api.getNodeRegion(it.node).rect },
      })),
    }));
    interactive.spacingState = {
      direction: targetHandler.direction,
      pairKey,
      aId: pair.aId,
      bId: pair.bId,
      nodesSorted: info.nodesSorted,
      initialRectsSorted,
      initialGap,
      minGap: 0,
      currentGap: initialGap,
      initialRowGroups,
      initialColGroups,
    };
    // Warm up initial caches for all nodes in involved groups for stable local-delta updates
    const allIds = new Set([
      ...initialRowGroups.flatMap((g) => g.items.map((it) => it.id)),
      ...initialColGroups.flatMap((g) => g.items.map((it) => it.id)),
    ]);
    allIds.forEach((id) => interactive.getMovingNodeInitialBox(id));
    return true;
  }

  if (type === EventTypes.move) {
    // Hover cursor over spacing handle
    if (targetHandler?.type === 'spacing') {
      interactive.cursor.set(targetHandler.direction === 'horizontal' ? 'ew-resize' : 'ns-resize');
      interactive.hoverHandlerId = targetHandler.id;
      stageContext.api.markNeedRender();
      return true;
    }
    if (targetHandler?.type === 'spacing-equalize') {
      interactive.cursor.set('pointer');
      interactive.hoverHandlerId = targetHandler.id;
      stageContext.api.markNeedRender();
      return true;
    }

    if (interactive.movingHandler?.type === 'spacing' && interactive.spacingState) {
      const { direction, initialGap, minGap, initialRowGroups, initialColGroups } =
        interactive.spacingState;
      const delta =
        direction === 'horizontal'
          ? point.x - interactive.pointStart.x
          : point.y - interactive.pointStart.y;
      const newGap = Math.max(minGap, initialGap + delta);
      if (direction === 'horizontal') {
        applyUniformGapUsingInitialRows(stageContext, initialRowGroups, newGap);
      } else {
        applyUniformGapUsingInitialCols(stageContext, initialColGroups, newGap);
      }
      // Update group sizes after applying spacing to avoid drift
      const touchedIds = new Set([
        ...initialRowGroups.flatMap((g) => g.items.map((it) => it.id)),
        ...initialColGroups.flatMap((g) => g.items.map((it) => it.id)),
      ]);
      const touchedNodes = Array.from(touchedIds)
        .map((id) => ({ node: stageContext.api.getNode(id) }))
        .filter((it) => it.node);
      updateGroupSize(stageContext, touchedNodes);
      interactive.spacingState.currentGap = Math.round(newGap);
      stageContext.hooks.onNodeMoveCallback();
      stageContext.api.markNeedRender();
      return true;
    }
  }

  if (type === EventTypes.up) {
    if (interactive.movingHandler?.type === 'spacing') {
      interactive.movingHandler = null;
      interactive.spacingState = null;
      interactive.cursor.reset();
      return true;
    }
  }

  return false;
}

function computeMultiSelectSpacingInfo(stageContext, explicitDirection) {
  const selectedNodes = stageContext.api.getSelectedNodes().filter((n) => n.type !== TYPES.frame);
  const pattern = analyzeLayoutPattern(stageContext, selectedNodes);
  const direction =
    explicitDirection || (pattern.patterns.isHorizontalRow ? 'horizontal' : 'vertical');
  const rects = selectedNodes.map((n) => stageContext.api.getNodeRegion(n).rect);
  const sortFn =
    direction === 'horizontal' ? (a, b) => a.rect.x - b.rect.x : (a, b) => a.rect.y - b.rect.y;
  const enriched = selectedNodes.map((n, i) => ({ node: n, rect: rects[i] })).sort(sortFn);
  const rectsSorted = enriched.map((e) => e.rect);
  const nodesSorted = enriched.map((e) => e.node);
  return { direction, nodesSorted, rectsSorted };
}

// Build groups helpers used by linked adjustments
export function computeRowGroups(stageContext) {
  const nodes = stageContext.api.getSelectedNodes().filter((n) => n.type !== TYPES.frame);
  const items = nodes.map((n) => ({ node: n, rect: stageContext.api.getNodeRegion(n).rect }));
  if (items.length < 2) return [];
  const rows = [];
  const threshold = 10;
  items.sort((a, b) => a.rect.y + a.rect.h / 2 - (b.rect.y + b.rect.h / 2));
  items.forEach((it) => {
    const cy = it.rect.y + it.rect.h / 2;
    let group = rows.find((r) => Math.abs(r.cy - cy) <= threshold);
    if (!group) {
      group = { cy, items: [] };
      rows.push(group);
    }
    group.items.push(it);
    group.cy = (group.cy * (group.items.length - 1) + cy) / group.items.length;
  });
  return rows;
}

export function computeColGroups(stageContext) {
  const nodes = stageContext.api.getSelectedNodes().filter((n) => n.type !== TYPES.frame);
  const items = nodes.map((n) => ({ node: n, rect: stageContext.api.getNodeRegion(n).rect }));
  if (items.length < 2) return [];
  const cols = [];
  const threshold = 10;
  items.sort((a, b) => a.rect.x + a.rect.w / 2 - (b.rect.x + b.rect.w / 2));
  items.forEach((it) => {
    const cx = it.rect.x + it.rect.w / 2;
    let group = cols.find((c) => Math.abs(c.cx - cx) <= threshold);
    if (!group) {
      group = { cx, items: [] };
      cols.push(group);
    }
    group.items.push(it);
    group.cx = (group.cx * (group.items.length - 1) + cx) / group.items.length;
  });
  return cols;
}

// Apply uniform gap using captured initial groups (rows). If targetList provided, use per-row target gap; else use uniform newGap
export function applyUniformGapUsingInitialRows(
  stageContext,
  initialRowGroups,
  newGap = null,
  targetList = null,
) {
  const interactive = stageContext.providers?.interactive || stageContext.interactive;
  initialRowGroups.forEach((g, idx) => {
    const items = [...g.items]
      .map((it) => ({ id: it.id, rect: { ...it.rect } }))
      .sort((a, b) => a.rect.x - b.rect.x);
    if (items.length < 2) return;
    const gap = targetList ? (targetList[idx]?.gap ?? 0) : (newGap ?? 0);
    let cursor = items[0].rect.x + items[0].rect.w; // stage coords
    for (let i = 1; i < items.length; i++) {
      const it = items[i];
      const targetXAbs = cursor + gap; // stage coords
      const deltaAbs = targetXAbs - it.rect.x; // desired delta in stage coords
      const cache = interactive?.getMovingNodeInitialBox?.(it.id);
      if (cache) {
        stageContext.api.updateNode(it.id, { x: cache.x + deltaAbs });
      } else {
        // Fallback: apply minimally using current local without absolute overwrite
        const node = stageContext.api.getNode(it.id);
        stageContext.api.updateNode(it.id, { x: Math.round((node?.x || 0) + deltaAbs) });
      }
      cursor = targetXAbs + it.rect.w;
    }
  });
}

// Apply uniform gap using captured initial groups (columns)
export function applyUniformGapUsingInitialCols(
  stageContext,
  initialColGroups,
  newGap = null,
  targetList = null,
) {
  const interactive = stageContext.providers?.interactive || stageContext.interactive;
  initialColGroups.forEach((g, idx) => {
    const items = [...g.items]
      .map((it) => ({ id: it.id, rect: { ...it.rect } }))
      .sort((a, b) => a.rect.y - b.rect.y);
    if (items.length < 2) return;
    const gap = targetList ? (targetList[idx]?.gap ?? 0) : (newGap ?? 0);
    let cursor = items[0].rect.y + items[0].rect.h; // stage coords
    for (let i = 1; i < items.length; i++) {
      const it = items[i];
      const targetYAbs = cursor + gap; // stage coords
      const deltaAbs = targetYAbs - it.rect.y; // desired delta in stage coords
      const cache = interactive?.getMovingNodeInitialBox?.(it.id);
      if (cache) {
        stageContext.api.updateNode(it.id, { y: cache.y + deltaAbs });
      } else {
        const node = stageContext.api.getNode(it.id);
        stageContext.api.updateNode(it.id, { y: Math.round((node?.y || 0) + deltaAbs) });
      }
      cursor = targetYAbs + it.rect.h;
    }
  });
}

export function applyPairGapShiftByGroup(
  stageContext,
  nodesSorted,
  initialRectsSorted,
  direction,
  aId,
  bId,
  shift,
) {
  // Determine group membership by same row (for horizontal) or same column (for vertical)
  const rectById = new Map();
  nodesSorted.forEach((n, i) => rectById.set(n.id, initialRectsSorted[i]));
  const aRect = rectById.get(aId);
  const bRect = rectById.get(bId);
  if (!aRect || !bRect) return;

  if (direction === 'horizontal') {
    // Same row: significant vertical overlap with a/b
    const isSameRow = (r) => {
      const top = Math.max(r.y, aRect.y, bRect.y);
      const bottom = Math.min(r.y + r.h, aRect.y + aRect.h, bRect.y + bRect.h);
      const overlap = Math.max(0, bottom - top);
      return overlap >= Math.min(r.h, aRect.h, bRect.h) * 0.4;
    };
    // Nodes to the right of b move by shift; left of or equal to a stay
    nodesSorted.forEach((node, i) => {
      const r = initialRectsSorted[i];
      const move = isSameRow(r) && r.x >= bRect.x; // move b and after
      const newX = move ? r.x + shift : r.x;
      if (stageContext.api.getNode(node.id).x !== newX) {
        stageContext.api.updateNode(node.id, { x: newX, y: r.y });
      }
    });
  } else {
    // vertical: same column -> significant horizontal overlap
    const isSameCol = (r) => {
      const left = Math.max(r.x, aRect.x, bRect.x);
      const right = Math.min(r.x + r.w, aRect.x + aRect.w, bRect.x + bRect.w);
      const overlap = Math.max(0, right - left);
      return overlap >= Math.min(r.w, aRect.w, bRect.w) * 0.4;
    };
    nodesSorted.forEach((node, i) => {
      const r = initialRectsSorted[i];
      const move = isSameCol(r) && r.y >= bRect.y; // move b and below
      const newY = move ? r.y + shift : r.y;
      if (stageContext.api.getNode(node.id).y !== newY) {
        stageContext.api.updateNode(node.id, { x: r.x, y: newY });
      }
    });
  }
}

// Group nodes into rows and build horizontal adjacent pairs within each row
function computeHorizontalPairs(stageContext) {
  const nodes = stageContext.api.getSelectedNodes().filter((n) => n.type !== TYPES.frame);
  const rects = nodes.map((n) => ({ node: n, rect: stageContext.api.getNodeRegion(n).rect }));
  if (rects.length < 2) return [];
  // cluster rows by y center proximity
  const rows = [];
  const threshold = 10; // px in stage coords
  rects.sort((a, b) => a.rect.y + a.rect.h / 2 - (b.rect.y + b.rect.h / 2));
  rects.forEach((item) => {
    const cy = item.rect.y + item.rect.h / 2;
    let group = rows.find((r) => Math.abs(r.cy - cy) <= threshold);
    if (!group) {
      group = { cy, items: [] };
      rows.push(group);
    }
    group.items.push(item);
    // update group center
    group.cy = (group.cy * (group.items.length - 1) + cy) / group.items.length;
  });
  // sort each row by x, build adjacent pairs
  const pairs = [];
  rows.forEach((row, rIndex) => {
    row.items.sort((a, b) => a.rect.x - b.rect.x);
    for (let i = 0; i < row.items.length - 1; i++) {
      const a = row.items[i],
        b = row.items[i + 1];
      pairs.push({
        key: `${rIndex}-${i}`,
        aId: a.node.id,
        bId: b.node.id,
        aRect: a.rect,
        bRect: b.rect,
        rowIndex: rIndex,
      });
    }
  });
  return pairs;
}

// Group nodes into columns and build vertical adjacent pairs within each column
function computeVerticalPairs(stageContext) {
  const nodes = stageContext.api.getSelectedNodes().filter((n) => n.type !== TYPES.frame);
  const rects = nodes.map((n) => ({ node: n, rect: stageContext.api.getNodeRegion(n).rect }));
  if (rects.length < 2) return [];
  const cols = [];
  const threshold = 10; // px in stage coords
  rects.sort((a, b) => a.rect.x + a.rect.w / 2 - (b.rect.x + b.rect.w / 2));
  rects.forEach((item) => {
    const cx = item.rect.x + item.rect.w / 2;
    let group = cols.find((c) => Math.abs(c.cx - cx) <= threshold);
    if (!group) {
      group = { cx, items: [] };
      cols.push(group);
    }
    group.items.push(item);
    group.cx = (group.cx * (group.items.length - 1) + cx) / group.items.length;
  });
  const pairs = [];
  cols.forEach((col, cIndex) => {
    col.items.sort((a, b) => a.rect.y - b.rect.y);
    for (let i = 0; i < col.items.length - 1; i++) {
      const a = col.items[i],
        b = col.items[i + 1];
      pairs.push({
        key: `${cIndex}-${i}`,
        aId: a.node.id,
        bId: b.node.id,
        aRect: a.rect,
        bRect: b.rect,
        colIndex: cIndex,
      });
    }
  });
  return pairs;
}

// 绘制多选的“间距调整”手柄（水平和垂直）
export function drawSpacingHandles(context, stageContext) {
  const selected = stageContext.api.getSelectedNodes();
  const nonFrameSelected = selected?.filter((n) => n.type !== TYPES.frame) || [];
  if (!nonFrameSelected || nonFrameSelected.length < 2) return;
  const { scale } = stageContext.providers;
  const interactive = stageContext.providers.interactive;
  const pairsH = computeHorizontalPairs(stageContext);
  const pairsV = computeVerticalPairs(stageContext);

  // Helper to draw pair handle as centered red line and translucent gap overlay
  const drawPair = (aRect, bRect, dir, pairKey, shouldHighlight, showLabel) => {
    const ax = aRect.x * scale.scale + scale.translateX;
    const ay = aRect.y * scale.scale + scale.translateY;
    const aw = aRect.w * scale.scale;
    const ah = aRect.h * scale.scale;
    const bx = bRect.x * scale.scale + scale.translateX;
    const by = bRect.y * scale.scale + scale.translateY;
    const bw = bRect.w * scale.scale;
    const bh = bRect.h * scale.scale;

    context.save();
    context.strokeStyle = 'rgba(255,0,0,1)';
    context.lineWidth = 2;

    if (dir === 'horizontal') {
      const gapStart = ax + aw;
      const gapEnd = bx;
      if (gapEnd > gapStart) {
        const midX = (gapStart + gapEnd) / 2;
        const overlapTop = Math.max(ay, by);
        const overlapBottom = Math.min(ay + ah, by + bh);
        const cy = (overlapTop + overlapBottom) / 2;
        // red line vertical with fixed length
        const lineLen = 18;
        context.beginPath();
        context.moveTo(midX, cy - lineLen / 2);
        context.lineTo(midX, cy + lineLen / 2);
        context.stroke();
        // During drag: highlight overlays for all linked pairs; label only for active pair
        if (shouldHighlight) {
          context.fillStyle = 'rgba(255,0,0,0.12)';
          context.fillRect(
            gapStart,
            overlapTop,
            gapEnd - gapStart,
            Math.max(2, overlapBottom - overlapTop),
          );
          if (showLabel) {
            const gapVal =
              typeof interactive.spacingState?.currentGap === 'number'
                ? interactive.spacingState.currentGap
                : Math.round((gapEnd - gapStart) / scale.scale);
            drawSpacingLabel(context, midX, overlapTop - 8, gapVal);
          }
        }
      }
    } else {
      const gapStart = ay + ah;
      const gapEnd = by;
      if (gapEnd > gapStart) {
        const midY = (gapStart + gapEnd) / 2;
        const overlapLeft = Math.max(ax, bx);
        const overlapRight = Math.min(ax + aw, bx + bw);
        const cx = (overlapLeft + overlapRight) / 2;
        // red line horizontal with fixed length
        const lineLen = 18;
        context.beginPath();
        context.moveTo(cx - lineLen / 2, midY);
        context.lineTo(cx + lineLen / 2, midY);
        context.stroke();
        if (shouldHighlight) {
          context.fillStyle = 'rgba(255,0,0,0.12)';
          context.fillRect(
            overlapLeft,
            gapStart,
            Math.max(2, overlapRight - overlapLeft),
            gapEnd - gapStart,
          );
          if (showLabel) {
            const gapVal =
              typeof interactive.spacingState?.currentGap === 'number'
                ? interactive.spacingState.currentGap
                : Math.round((gapEnd - gapStart) / scale.scale);
            drawSpacingLabel(context, overlapRight + 8, midY + 4, gapVal);
          }
        }
      }
    }

    context.restore();
  };

  // Determine which pairs to highlight during drag (linked), disambiguate H/V with prefixes
  const overlayKeys = new Set();
  let labelKey = null;
  if (interactive.movingHandler?.type === 'spacing') {
    const activePairKey = interactive.movingHandler.pairKey;
    const isH = interactive.movingHandler.direction === 'horizontal';
    const prefix = isH ? 'h:' : 'v:';
    labelKey = `${prefix}${activePairKey}`;
    if (isH) {
      const active = pairsH.find((p) => p.key === activePairKey);
      if (active) {
        pairsH
          .filter((p) => p.rowIndex === active.rowIndex)
          .forEach((p) => overlayKeys.add(`h:${p.key}`));
      }
    } else {
      const active = pairsV.find((p) => p.key === activePairKey);
      if (active) {
        pairsV
          .filter((p) => p.colIndex === active.colIndex)
          .forEach((p) => overlayKeys.add(`v:${p.key}`));
      }
    }
  }

  // Draw between adjacent pairs per row (horizontal)
  pairsH.forEach((p) =>
    drawPair(
      p.aRect,
      p.bRect,
      'horizontal',
      p.key,
      overlayKeys.has(`h:${p.key}`),
      labelKey === `h:${p.key}`,
    ),
  );
  // Draw between adjacent pairs per column (vertical)
  pairsV.forEach((p) =>
    drawPair(
      p.aRect,
      p.bRect,
      'vertical',
      p.key,
      overlayKeys.has(`v:${p.key}`),
      labelKey === `v:${p.key}`,
    ),
  );

  // Quick equalize button at selection bottom-right when gaps are inconsistent
  const bounds = stageContext.api.getSelectedNodesRegion();
  const br = (bounds.x + bounds.w) * scale.scale + scale.translateX;
  const bb = (bounds.y + bounds.h) * scale.scale + scale.translateY;
  const btnW = 32,
    btnH = 32,
    pad = 16;
  const btnX = br - btnW - pad,
    btnY = bb - btnH - pad;
  const inconsistentH = hasInconsistentGaps(pairsH, 'horizontal');
  const inconsistentV = hasInconsistentGaps(pairsV, 'vertical');
  if (inconsistentH || inconsistentV) {
    drawEqualizeButton(context, btnX, btnY, btnW, btnH);
    // Store for hit-test
    stageContext.interactive.equalizeButtonRect = {
      x: (btnX - scale.translateX) / scale.scale,
      y: (btnY - scale.translateY) / scale.scale,
      w: btnW / scale.scale,
      h: btnH / scale.scale,
    };
  } else {
    stageContext.interactive.equalizeButtonRect = null;
  }
}

// 生成“间距手柄”的命中区域，复用到 getInteractiveHandlers(selection)
export function getSpacingHandlers(stageContext, region, _scale = 1) {
  // Handlers for each adjacent pair per row/column (stage coords)
  const handlers = [];
  const pairsH = computeHorizontalPairs(stageContext);
  const pairsV = computeVerticalPairs(stageContext);

  const r = 6;
  pairsH.forEach((p) => {
    const a = p.aRect,
      b = p.bRect;
    const gapStart = a.x + a.w;
    const gapEnd = b.x;
    if (gapEnd > gapStart) {
      const overlapTop = Math.max(a.y, b.y);
      const overlapBottom = Math.min(a.y + a.h, b.y + b.h);
      const midX = (gapStart + gapEnd) / 2;
      const rect = new Rect(midX - r, (overlapTop + overlapBottom) / 2 - r, r * 2, r * 2);
      rect.id = `spacing-h-${p.key}`;
      rect.type = 'spacing';
      rect.direction = 'horizontal';
      rect.pairKey = p.key;
      rect.aId = p.aId;
      rect.bId = p.bId;
      handlers.push(rect);
    }
  });

  pairsV.forEach((p) => {
    const a = p.aRect,
      b = p.bRect;
    const gapStart = a.y + a.h;
    const gapEnd = b.y;
    if (gapEnd > gapStart) {
      const overlapLeft = Math.max(a.x, b.x);
      const overlapRight = Math.min(a.x + a.w, b.x + b.w);
      const midY = (gapStart + gapEnd) / 2;
      const rect = new Rect((overlapLeft + overlapRight) / 2 - r, midY - r, r * 2, r * 2);
      rect.id = `spacing-v-${p.key}`;
      rect.type = 'spacing';
      rect.direction = 'vertical';
      rect.pairKey = p.key;
      rect.aId = p.aId;
      rect.bId = p.bId;
      handlers.push(rect);
    }
  });

  // Add equalize button as a handler when present
  const btn = stageContext.interactive.equalizeButtonRect;
  if (btn) {
    const rect = new Rect(btn.x, btn.y, btn.w, btn.h);
    rect.id = 'spacing-equalize';
    rect.type = 'spacing-equalize';
    handlers.push(rect);
  }
  return handlers;
}

function drawSpacingLabel(context, x, y, value) {
  context.save();
  const text = `${value}`;
  context.font = '12px ui-sans-serif, -apple-system, Arial';
  const w = context.measureText(text).width + 8;
  const h = 18;
  context.fillStyle = 'rgba(255,0,0,0.9)';
  context.beginPath();
  const r = 6;
  context.moveTo(x - w / 2 + r, y - h);
  context.arcTo(x + w / 2, y - h, x + w / 2, y, r);
  context.arcTo(x + w / 2, y, x - w / 2, y, r);
  context.arcTo(x - w / 2, y, x - w / 2, y - h, r);
  context.arcTo(x - w / 2, y - h, x + w / 2, y - h, r);
  context.closePath();
  context.fill();
  context.fillStyle = '#fff';
  context.fillText(text, x - w / 2 + 4, y - 4);
  context.restore();
}

function hasInconsistentGaps(pairs, direction, tolerance = 0.5) {
  if (!pairs || pairs.length < 2) return false;
  const gaps = pairs.map((p) =>
    direction === 'horizontal'
      ? Math.max(0, p.bRect.x - (p.aRect.x + p.aRect.w))
      : Math.max(0, p.bRect.y - (p.aRect.y + p.aRect.h)),
  );
  const avg = gaps.reduce((s, g) => s + g, 0) / gaps.length;
  return gaps.some((g) => Math.abs(g - avg) > tolerance);
}

function drawEqualizeButton(context, x, y, w, h) {
  context.save();
  // button background
  context.fillStyle = 'rgba(139,62,255,0.95)';
  const r = 6;
  context.beginPath();
  context.moveTo(x + r, y);
  context.arcTo(x + w, y, x + w, y + h, r);
  context.arcTo(x + w, y + h, x, y + h, r);
  context.arcTo(x, y + h, x, y, r);
  context.arcTo(x, y, x + w, y, r);
  context.closePath();
  context.fill();
  // icon: two short parallel lines to indicate equal spacing
  context.strokeStyle = '#fff';
  context.lineWidth = 2;
  const cx = x + w / 2;
  const cy = y + h / 2;
  const len = Math.min(w, h) * 0.5;
  context.beginPath();
  context.moveTo(cx - len / 2, cy - 4);
  context.lineTo(cx + len / 2, cy - 4);
  context.moveTo(cx - len / 2, cy + 4);
  context.lineTo(cx + len / 2, cy + 4);
  context.stroke();
  context.restore();
}
